/**
 * Patient app legal documents hook
 *
 * Loads German privacy and liability copy from `appInfo/patientAppLegal` for checkout
 * consent and liability drawers.
 */

import type { PatientAppLegal } from '@physiofit-digital/physiofit_types';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../firebaseConfig';

export type PatientAppLegalDe = {
  privacyUrl: string;
  liabilityTitle: string;
  liabilityText: string;
};

export type PatientAppLegalResult =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; legal: PatientAppLegalDe };

function pickLocalizedString(
  value: { de?: string; en?: string } | undefined,
  options?: { requireDe?: boolean },
): string {
  if (!value) {
    return '';
  }
  const de = typeof value.de === 'string' ? value.de.trim() : '';
  if (de) {
    return de;
  }
  if (options?.requireDe) {
    return '';
  }
  const en = typeof value.en === 'string' ? value.en.trim() : '';
  return en;
}

function normalizeLegalText(raw: string): string {
  return raw.replace(/\\n/g, '\n').trim();
}

function parsePatientAppLegal(data: PatientAppLegal): PatientAppLegalDe | null {
  const privacyUrl = pickLocalizedString(data.privacyInfo?.url, { requireDe: true });
  const liabilityTitle = pickLocalizedString(data.liabilityInfo?.title);
  const liabilityText = normalizeLegalText(pickLocalizedString(data.liabilityInfo?.text));

  if (!privacyUrl || !liabilityTitle || !liabilityText) {
    return null;
  }

  return { privacyUrl, liabilityTitle, liabilityText };
}

const patientAppLegalDocRef = () => doc(db, 'appInfo', 'patientAppLegal');

/**
 * Loads patient legal URLs and liability text once on mount.
 *
 * @return {Object} Loading, error, or ready legal payload.
 */
export function usePatientAppLegal(): PatientAppLegalResult {
  const [result, setResult] = useState<PatientAppLegalResult>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const snap = await getDoc(patientAppLegalDocRef());
        if (cancelled) {
          return;
        }
        if (!snap.exists()) {
          setResult({ status: 'error' });
          return;
        }
        const legal = parsePatientAppLegal(snap.data() as PatientAppLegal);
        if (!legal) {
          setResult({ status: 'error' });
          return;
        }
        setResult({ status: 'ready', legal });
      } catch {
        if (!cancelled) {
          setResult({ status: 'error' });
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}
